/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 埋点相关工具函数
 */
import dayjs from 'dayjs'
import { FilterState } from '../types'
import CONFIG from '../../../../constants/config'
import sensors, { sensorsRole } from '../../../../utils/sensors'
import { store } from '../../../../app/store'
import type { RootState } from '../../../../app/store'
import { applyPicPageClickSupplementFields } from '../../../ai-photo-king/utils/picPageClickEnhancements'
import { calcPickedMemberNum } from './filterUtils'

/**
 * 生成推荐词来源字符串
 */
export const generateRecommendedWordSource = (keywords: string[]): string => {
  // 移除地点推荐逻辑，统一使用 photo 来源
  return Array(keywords.length).fill('photo').join('|')
}

/**
 * 筛选提交埋点
 */
export const trackFilterSubmit = (params: {
  filter: FilterState
  groupId: string
  myRole: number
  members: any
  allWatermarksNum: number
  pageType?: string
  labelName?: string
}) => {
  const {
    filter,
    groupId,
    myRole,
    members,
    allWatermarksNum,
    pageType = 'allPhoto',
    labelName
  } = params

  const dateOffset = filter.times
    ? dayjs(filter.times[1]).diff(dayjs(filter.times[0]), 'days') + 1
    : 0

  // 获取成员总数
  const membersSize =
    typeof members?.size === 'number'
      ? members.size
      : typeof members?.length === 'number'
        ? members.length
        : 0

  // 计算选中的成员数量
  const memberNum = calcPickedMemberNum(filter, membersSize)

  // 判断是否选择了全部成员
  const isAllMember = memberNum === membersSize

  // 获取地点信息
  const photoLocation =
    (filter.locations && filter.locations[0] && filter.locations[0].name) || ''

  sensors.track('web_workgroup_picspage_click_filter', {
    searchContent: filter.searchKeywords,
    fileType:
      filter.mediaType === null
        ? 'all'
        : filter.mediaType === 0
          ? 'photo'
          : 'video',
    dateOffset,
    startTime: filter.times?.[0]?.format(CONFIG.timeFormat) || '',
    endTime: filter.times?.[1]?.format(CONFIG.timeFormat) || '',
    memberNum,
    isAllMember,
    watermarkNum: filter.watermarks.length,
    isAllWatermark:
      filter.watermarks.length === 0 ||
      filter.watermarks.length === allWatermarksNum,
    photoLocation,
    role: sensorsRole(myRole),
    groupID: groupId,
    recommendedWordSource: generateRecommendedWordSource(filter.searchKeywords),
    pageType,
    ...(labelName !== undefined ? { labelName } : {})
  })
}

/**
 * 筛选成功埋点
 */
export const trackFilterSuccess = (params: {
  processTime: number
  firstPageNum: number
  recommendParticipleList: string[]
  keyword: string
  recommendedWordSource: string
  list1: number[]
  fromPlace?: string
  timeRule: string[]
  number1: number
  list2: string[]
  string3: string
  searchResultType?: string
  searchText?: string
  string1?: string
  list3?: string[]
  TextContent?: string
  list4?: string[]
  role?: string
  groupID?: string
  fileType?: string
  /** 20260414：与下方 trackFilterSuccess 内自动合并的字段一致 */
  number4?: number
  number5?: number
  number6?: number
}) => {
  const state = store.getState() as RootState
  const relatedNum = state.photosByContent?.suggestedPhotosNum ?? 0
  const payload: Record<string, any> = { ...params }
  if (relatedNum > 0) {
    const fb = state.photosByContent?.fallbackType ?? 'none'
    if (fb === 'P2') {
      payload.number5 = relatedNum
    } else if (fb === 'P3') {
      payload.number6 = relatedNum
    } else {
      payload.number4 = relatedNum
    }
  }
  sensors.track('web_workgroup_picspage_filter_suc', payload)
}

/**
 * 筛选时间点击埋点
 */
export const trackFilterTimeClick = () => {
  sensors.track('web_workgroup_filter_time_click', {
    pagePath: window.location.pathname
  })
}

/**
 * 筛选水印完成埋点
 */
export const trackWatermarkFilterDone = (params: {
  watermarkNum: number
  groupWatermarkNum: number
  basicWatermarkNum: number
  noWatermarkNum: number
  pageType: string
}) => {
  sensors.track('web_workgroup_filter_watermark_page_click_done', params)
}

/**
 * 照片页面点击埋点
 */
export const trackPicsPageClick = (params: {
  clickItem: string
  isOpenErrorCorrection?: boolean
  requirement_id?: number
  [key: string]: any
}) => {
  const { clickItem, isOpenErrorCorrection, ...restProperties } = params

  // 使用默认值，避免调用方修改关键字段
  const requirement_id = 273
  const track_sign = 'aworkgroup.b4343.action.web_workgroup_picspage_click'
  const state = store.getState()
  const groupID = (state?.group?.groupId as string) || ''
  const roleNum = (state?.group?.myRole as number) || 0
  const role = sensorsRole(roleNum)

  const trackParams: Record<string, any> = {
    clickItem,
    groupID,
    role,
    track_sign,
    requirement_id,
    ...restProperties
  }
  if (Object.prototype.hasOwnProperty.call(params, 'isOpenErrorCorrection')) {
    trackParams.isOpenErrorCorrection = isOpenErrorCorrection
  }

  applyPicPageClickSupplementFields(trackParams, state as RootState, clickItem)

  sensors.track('web_workgroup_picspage_click', trackParams)
}

/**
 * 标签创建成功埋点
 */
export const trackLabelCreateSuccess = (params: {
  labelName: string
  labelClassification: string
  categoryName: '团队标签' | '自定义标签'
  fromPlace: '全部照片筛选标签页' | '全部照片批量添加照片页' | '标签管理页'
}) => {
  sensors.track('workgroup_b8085_completed', {
    requirement_id: 1059,
    track_sign: 'aworkgroup.b8085.completed.workgroup_b8085_completed',
    labelName: params.labelName,
    labelClassification: params.labelClassification,
    categoryName: params.categoryName,
    fromPlace: params.fromPlace
  })
}

/**
 * 照片标签页行为埋点
 */
export const trackPhotoTagPageEvent = (params: {
  actionType:
    | '进入页面'
    | '点击新建标签输入框'
    | '创建标签'
    | '点击新增标签组'
    | '新增标签组成功'
    | '开启强制打标签'
    | '关闭强制打标签'
    | '开启非强制提醒'
    | '关闭非强制提醒'
  tabName: '团队标签' | '个人标签' | '下发配置'
  number1?: number
  number2?: number
  string1?: string
  string2?: string
}) => {
  sensors.track('workgroup_b8086_completed', {
    requirement_id: 1059,
    track_sign: 'aworkgroup.b8086.completed.workgroup_b8086_completed',
    actionType: params.actionType,
    tabName: params.tabName,
    ...(params.number1 !== undefined ? { number1: params.number1 } : {}),
    ...(params.number2 !== undefined ? { number2: params.number2 } : {}),
    ...(params.string1 !== undefined ? { string1: params.string1 } : {}),
    ...(params.string2 !== undefined ? { string2: params.string2 } : {})
  })
}

/**
 * 照片打标签成功埋点
 */
export const trackLabelBindSuccess = (params: {
  photoNum: number
  labelName: string
  fromPlace: '全部照片页批量打标签' | '照片预览页打标签'
}) => {
  sensors.track('workgroup_b8084_completed', {
    requirement_id: 1059,
    track_sign: 'aworkgroup.b8084.completed.workgroup_b8084_completed',
    labelName: params.labelName,
    fromPlace: params.fromPlace,
    photoNum: params.photoNum
  })
}
